import { Box, Link, Tabs, Text, VStack } from '@chakra-ui/react';
import type { ReactNode } from 'react'
import ReactMarkdown from 'react-markdown'
import type { LobbyPacketDto } from '../../types/lobbyDay'
import { LegislatorResourceLinks } from './LegislatorResourceLinks'
import { BillContentDetails } from './BillContentDetails'
import { LegiScanCredit } from './LegiScanCredit'
import { proseContentCss } from '../../theme/proseStyles'

interface PacketContentProps {
  packet: LobbyPacketDto
  /** LD-T4-5: each host supplies its own download URL — the organizer cookie route
   *  (lobbyAttachmentApi.fileUrl) or the attendee token route (lobbyAttendeeApi.attachmentUrl). */
  attachmentHref?: (id: string) => string
}

/** Human-readable file size for a packet document row. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

/** The composed packet body. Used by the organizer preview drawer AND the
 *  attendee mobile packet page — this is the one place packet tabs are defined. */
export function PacketContent({ packet, attachmentHref }: PacketContentProps) {
  const hasLinks = !!(packet.ballotpediaUrl || packet.voteSmartUrl || packet.openSecretsUrl || packet.followTheMoneyUrl)
  const tabs: { label: string; markdown?: string; node?: ReactNode }[] = []
  if (packet.talkingPointsMarkdown) tabs.push({ label: 'Talking points', markdown: packet.talkingPointsMarkdown })
  if (packet.linkedBills.length > 0 || packet.billMarkdown) {
    tabs.push({
      label: packet.billNumber ?? 'Bill',
      node: (
        <VStack align="stretch" gap={3}>
          {packet.linkedBills.map((b) => (
            <Box key={b.billNumber}>
              <Text fontWeight="semibold" fontSize="sm">
                Official summary{b.billNumber ? ` · ${b.billNumber}` : ''}
              </Text>
              <BillContentDetails summary={b.summary} fullTextUrl={b.fullTextUrl} />
            </Box>
          ))}
          {packet.linkedBills.length > 0 && <LegiScanCredit />}
          {!!packet.billMarkdown && (
            <Box>
              <Text fontWeight="semibold" fontSize="sm">Bill notes</Text>
              <ReactMarkdown>{packet.billMarkdown}</ReactMarkdown>
            </Box>
          )}
        </VStack>
      ),
    })
  }
  if (packet.legislatorBackgroundMarkdown || hasLinks) {
    tabs.push({
      label: 'Legislator',
      markdown: packet.legislatorBackgroundMarkdown ?? undefined,
      node: (
        <LegislatorResourceLinks
          ballotpediaUrl={packet.ballotpediaUrl}
          voteSmartUrl={packet.voteSmartUrl}
          openSecretsUrl={packet.openSecretsUrl}
          followTheMoneyUrl={packet.followTheMoneyUrl}
        />
      ),
    })
  }
  if (packet.meetingNotesMarkdown) tabs.push({ label: 'Notes', markdown: packet.meetingNotesMarkdown })

  if (packet.attachments.length > 0 && attachmentHref) {
    tabs.push({
      label: 'Documents',
      node: (
        <VStack align="stretch" gap={2}>
          {packet.attachments.map((a) => (
            <Link key={a.id} href={attachmentHref(a.id)} minH="44px" display="flex"
                  alignItems="center" justifyContent="space-between" px={2} py={2}
                  borderWidth="1px" borderRadius="md">
              <Text fontSize="sm" fontWeight="medium">{a.title}</Text>
              <Text fontSize="xs" color="fg.muted">{formatBytes(a.sizeBytes)}</Text>
            </Link>
          ))}
        </VStack>
      ),
    })
  }

  if (tabs.length === 0) {
    return <Text color="gray.500" py={6} textAlign="center">No packet content yet for this meeting.</Text>
  }

  return (
    <Tabs.Root colorPalette="brand" size="sm" fitted defaultValue={tabs[0].label}>
      <Tabs.List>{tabs.map((t) => <Tabs.Trigger key={t.label} value={t.label}>{t.label}</Tabs.Trigger>)}</Tabs.List>
      <Tabs.ContentGroup>
        {tabs.map((t) => (
          <Tabs.Content key={t.label} value={t.label} px={1}>
            <Box css={proseContentCss}>
              {!!t.markdown && <ReactMarkdown>{t.markdown}</ReactMarkdown>}
              {t.node}
            </Box>
          </Tabs.Content>
        ))}
      </Tabs.ContentGroup>
    </Tabs.Root>
  );
}
